// The Black & White product barcode label: what goes on a label and how one
// label card is drawn. The sheet around it is drawn by labelSheetDocument.js.
//
// Physical output must not drift: the card markup and CSS below are the ones
// the catalog label sheet has always printed with.

// Fixed card internals (millimetres / points), unchanged from the original sheet.
export const BARCODE_LABEL_TEMPLATE = Object.freeze({
  id: "bw-barcode-label",
  cardPadMM: 2.2,
  barcodePadXMM: 1.6,
});

// Which price the label shows. `regular-first` is the historical behaviour
// (the regular "compare at" price when set, otherwise the selling price) and
// stays the default so existing labels do not silently change.
export const LABEL_PRICE_POLICIES = Object.freeze({
  REGULAR_FIRST: "regular-first",
  SELLING: "selling",
  HIDDEN: "hidden",
});

export const DEFAULT_LABEL_CONTENT = Object.freeze({
  pricePolicy: LABEL_PRICE_POLICIES.REGULAR_FIRST,
  title: Object.freeze({ fontSizePt: 19.7, fontWeight: 600, align: "center", maxLines: 1, overflow: "clip" }),
  color: Object.freeze({ fontSizePt: 13.5, fontWeight: 600, align: "center" }),
  size: Object.freeze({ fontSizePt: 22, fontWeight: 700, align: "left" }),
  price: Object.freeze({ fontSizePt: 5.8, fontWeight: 700, align: "center", visible: true }),
  barcode: Object.freeze({ widthPercent: 100, heightMM: 9, sidePaddingMM: 1.6, textSizePt: 5.4, showText: true, spacingMM: 0.55, format: "CODE128" }),
});

const numberIn = (value, fallback, min, max) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.min(max, Math.max(min, numeric)) : fallback;
};
const weight = (value, fallback) => [400, 500, 600, 700, 800, 900].includes(Number(value)) ? Number(value) : fallback;
const align = (value, fallback) => ["left", "center", "right"].includes(value) ? value : fallback;

export const normalizeLabelContent = (content) => ({
  pricePolicy: Object.values(LABEL_PRICE_POLICIES).includes(content?.pricePolicy) ? content.pricePolicy : DEFAULT_LABEL_CONTENT.pricePolicy,
  title: {
    fontSizePt: numberIn(content?.title?.fontSizePt, DEFAULT_LABEL_CONTENT.title.fontSizePt, 5, 36),
    fontWeight: weight(content?.title?.fontWeight, DEFAULT_LABEL_CONTENT.title.fontWeight),
    align: align(content?.title?.align, DEFAULT_LABEL_CONTENT.title.align),
    maxLines: Math.round(numberIn(content?.title?.maxLines, DEFAULT_LABEL_CONTENT.title.maxLines, 1, 3)),
    overflow: content?.title?.overflow === "ellipsis" ? "ellipsis" : "clip",
  },
  color: {
    fontSizePt: numberIn(content?.color?.fontSizePt, DEFAULT_LABEL_CONTENT.color.fontSizePt, 5, 28),
    fontWeight: weight(content?.color?.fontWeight, DEFAULT_LABEL_CONTENT.color.fontWeight),
    align: align(content?.color?.align, DEFAULT_LABEL_CONTENT.color.align),
  },
  size: {
    fontSizePt: numberIn(content?.size?.fontSizePt, DEFAULT_LABEL_CONTENT.size.fontSizePt, 5, 36),
    fontWeight: weight(content?.size?.fontWeight, DEFAULT_LABEL_CONTENT.size.fontWeight),
    align: align(content?.size?.align, DEFAULT_LABEL_CONTENT.size.align),
  },
  price: {
    fontSizePt: numberIn(content?.price?.fontSizePt, DEFAULT_LABEL_CONTENT.price.fontSizePt, 5, 24),
    fontWeight: weight(content?.price?.fontWeight, DEFAULT_LABEL_CONTENT.price.fontWeight),
    align: align(content?.price?.align, DEFAULT_LABEL_CONTENT.price.align),
    visible: content?.price?.visible !== false,
  },
  barcode: {
    widthPercent: numberIn(content?.barcode?.widthPercent, DEFAULT_LABEL_CONTENT.barcode.widthPercent, 40, 100),
    heightMM: numberIn(content?.barcode?.heightMM, DEFAULT_LABEL_CONTENT.barcode.heightMM, 6, 25),
    sidePaddingMM: numberIn(content?.barcode?.sidePaddingMM, DEFAULT_LABEL_CONTENT.barcode.sidePaddingMM, 0, 10),
    textSizePt: numberIn(content?.barcode?.textSizePt, DEFAULT_LABEL_CONTENT.barcode.textSizePt, 5, 14),
    showText: content?.barcode?.showText !== false,
    spacingMM: numberIn(content?.barcode?.spacingMM, DEFAULT_LABEL_CONTENT.barcode.spacingMM, 0, 5),
    format: "CODE128",
  },
});

// Mirrors the backend classifier (catalog-option-dimension.util.ts): option
// names are merchant-typed and localized ("Ngjyra" / "Madhësia").
const COLOR_DIMENSION_KEYS = new Set([
  "color",
  "colors",
  "colour",
  "colours",
  "ngjyra",
  "ngjyre",
  "ngjyrat",
  "ngjyres",
]);
const SIZE_DIMENSION_KEYS = new Set([
  "size",
  "sizes",
  "number",
  "numbers",
  "madhesia",
  "madhesi",
  "madhesite",
  "masa",
  "masat",
  "numri",
  "numrat",
  "nr",
]);

const normalizeText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

/** Lowercases, strips diacritics and separators: "Madhësia" → "madhesia". */
export const normalizeDimensionKey = (value) =>
  String(value ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

export const classifyOptionDimension = (...names) => {
  for (const name of names) {
    const key = normalizeDimensionKey(name);
    if (!key) continue;
    if (COLOR_DIMENSION_KEYS.has(key)) return "color";
    if (SIZE_DIMENSION_KEYS.has(key)) return "size";
  }
  return null;
};

export const findOptionValue = (optionValues, kind) =>
  normalizeText(
    (Array.isArray(optionValues) ? optionValues : []).find(
      (entry) => classifyOptionDimension(entry?.optionKey, entry?.optionName) === kind
    )?.value
  );

const stripProductPrefix = (variantTitle, productTitle) => {
  const cleanVariant = normalizeText(variantTitle);
  const cleanProduct = normalizeText(productTitle);
  if (!cleanVariant) return "";
  if (!cleanProduct) return cleanVariant;

  const variantLower = cleanVariant.toLowerCase();
  const productLower = cleanProduct.toLowerCase();
  if (variantLower === productLower) return "";

  for (const separator of [" / ", " - ", " · ", "|"]) {
    const prefix = `${productLower}${separator}`;
    if (variantLower.startsWith(prefix)) {
      return cleanVariant.slice(prefix.length).trim();
    }
  }
  return cleanVariant;
};

// Last resort for items that carry only a title (e.g. "Black / 38").
const splitVariantTokens = (variantTitle, productTitle) =>
  stripProductPrefix(variantTitle, productTitle)
    .split("/")
    .map((token) => normalizeText(token))
    .filter(Boolean);

const inferSizeFromTokens = (tokens) =>
  tokens.find((token) =>
    /^(?:\d{1,2}(?:[.,]\d)?|xxs|xs|s|m|l|xl|xxl|xxxl|one size)$/i.test(token)
  ) || "";

const inferColorFromTokens = (tokens, size) =>
  tokens.find(
    (token) => token && normalizeDimensionKey(token) !== normalizeDimensionKey(size)
  ) || "";

const MODEL_CODE_SEGMENT = /^\d{3,8}[A-Z]?$/i;

const inferModelCode = (item) => {
  for (const candidate of [item?.modelCode, item?.productCode, item?.baseSku, item?.code]) {
    const normalized = normalizeText(candidate);
    if (normalized) return normalized.toUpperCase();
  }

  for (const source of [item?.sku, item?.barcode]) {
    const segment = normalizeText(source)
      .split(/[-_/]/)
      .map((part) => normalizeText(part))
      .find((part) => MODEL_CODE_SEGMENT.test(part));
    if (segment) return segment.toUpperCase();
  }

  return "";
};

const colorNameToTone = (value) => {
  const normalized = normalizeDimensionKey(value);
  if (!normalized) return "#d6d0c6";

  let hash = 0;
  for (let index = 0; index < normalized.length; index += 1) {
    hash = (hash * 31 + normalized.charCodeAt(index)) % 256;
  }
  const hex = (176 + (hash % 48)).toString(16).padStart(2, "0");
  return `#${hex}${hex}${hex}`;
};

export const formatLabelPrice = (value) => {
  if (value == null || value === "") return "";

  if (typeof value === "string") {
    const normalized = normalizeText(value);
    if (!normalized) return "";
    const numeric = Number(normalized.replace(/[^\d.,-]/g, "").replace(",", "."));
    return Number.isFinite(numeric) ? `${numeric.toFixed(2)}€` : normalized;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric.toFixed(2)}€` : "";
};

export const resolveLabelPrice = (item, content = DEFAULT_LABEL_CONTENT) => {
  const { pricePolicy, price } = normalizeLabelContent(content);
  if (!price.visible) return "";
  if (pricePolicy === LABEL_PRICE_POLICIES.HIDDEN) return "";
  if (pricePolicy === LABEL_PRICE_POLICIES.SELLING) {
    return formatLabelPrice(item?.price ?? item?.priceText ?? item?.unitPriceAmount);
  }
  return formatLabelPrice(
    item?.compareAtPrice ?? item?.price ?? item?.priceText ?? item?.unitPriceAmount
  );
};

/** Everything a barcode label shows, resolved from a print item. */
export const getBarcodeLabelData = (item, content = DEFAULT_LABEL_CONTENT) => {
  const productName = normalizeText(
    item?.name || item?.productName || item?.productTitle || item?.title
  );
  const tokens = splitVariantTokens(item?.variantTitle, productName);
  const size =
    normalizeText(item?.size || item?.sizeLabel) ||
    findOptionValue(item?.optionValues, "size") ||
    inferSizeFromTokens(tokens) ||
    "—";
  const color =
    normalizeText(item?.color || item?.colorName) ||
    findOptionValue(item?.optionValues, "color") ||
    inferColorFromTokens(tokens, size) ||
    "—";

  return {
    productName,
    modelCode: inferModelCode(item),
    color,
    size,
    barcode: normalizeText(item?.barcode),
    price: resolveLabelPrice(item, content),
    colorTone: colorNameToTone(color),
    // Only present when a design that uses it was given a link (see textileModern).
    ...(item?.qrUrl ? { qrUrl: normalizeText(item.qrUrl) } : {}),
  };
};

// The footwear card itself lives in labelTemplates/footwearClassic.js; these
// re-exports keep older imports working.
export { renderBarcodeLabelCard, BARCODE_LABEL_CARD_CSS } from "./labelTemplates/footwearClassic";

/** Width available to the barcode bars inside one card. */
export const getBarcodeDrawableWidthMM = (labelWidthMM, geometry, content) => {
  const normalized = normalizeLabelContent(content);
  const left = Number(geometry?.paddingLeftMM ?? BARCODE_LABEL_TEMPLATE.cardPadMM);
  const right = Number(geometry?.paddingRightMM ?? BARCODE_LABEL_TEMPLATE.cardPadMM);
  const contentWidthMM = labelWidthMM - left - right;
  return contentWidthMM * normalized.barcode.widthPercent / 100 - normalized.barcode.sidePaddingMM * 2;
};

/** Conservative vertical fit check so typography controls cannot overlap. */
export const validateLabelContentFit = (geometry, content) => {
  const normalized = normalizeLabelContent(content);
  const drawableBarcodeWidth = getBarcodeDrawableWidthMM(
    Number(geometry?.labelWidthMM || 0),
    geometry,
    normalized
  );
  const usableHeight = Number(geometry?.labelHeightMM || 0) - Number(geometry?.paddingTopMM || 0) - Number(geometry?.paddingBottomMM || 0);
  const ptToMM = 25.4 / 72;
  const titleHeight = normalized.title.fontSizePt * ptToMM * 1.05 * normalized.title.maxLines;
  const variantHeight = Math.max(normalized.color.fontSizePt, normalized.size.fontSizePt) * ptToMM;
  const barcodeTextHeight = normalized.barcode.showText ? normalized.barcode.textSizePt * ptToMM * 1.15 : 0;
  const priceHeight = normalized.price.visible && normalized.pricePolicy !== LABEL_PRICE_POLICIES.HIDDEN ? normalized.price.fontSizePt * ptToMM : 0;
  const requiredHeight = titleHeight + variantHeight + normalized.barcode.heightMM + barcodeTextHeight + priceHeight + normalized.barcode.spacingMM + 3.2;
  const issues = [];
  if (drawableBarcodeWidth <= 0) issues.push("barcode_width_not_positive");
  if (requiredHeight > usableHeight + 0.001) issues.push("content_height_overflow");
  return issues;
};
